import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Table } from 'react-bootstrap';
import { Modal } from '../patterns';
import { SprayProduct, SprayProgram } from '../../services/sprayService';
import IdChip from './IdChip';

interface Props {
  program: SprayProgram;
  products: SprayProduct[];
  show: boolean;
  onHide: () => void;
}

const STATUS_VARIANT: Record<string, string> = {
  open: 'success',
  locked: 'warning',
  deleted: 'secondary',
};

const ProgramDetailModal: React.FC<Props> = ({ program, products, show, onHide }) => {
  const { t } = useTranslation();

  const productMap = useMemo(
    () => new Map(products.map(p => [p._id, p.productName])),
    [products]
  );

  const status = program.status || 'open';

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      title={
        <span className="d-flex align-items-center gap-2">
          {program.name}
          <IdChip prefix="PGM" seq={program.seq} />
          <Badge bg={STATUS_VARIANT[status] || 'secondary'}>
            {t(`spray.programs.status${status.charAt(0).toUpperCase() + status.slice(1)}`)}
          </Badge>
        </span>
      }
    >
      <Modal.Body>
        <p>
          <strong>{t('spray.programs.plannedDate')}:</strong>{' '}
          {program.plannedDate.substring(0, 10)}
        </p>

        {program.comments && (
          <p>
            <strong>{t('spray.programs.comments')}:</strong>{' '}
            {program.comments}
          </p>
        )}

        <p>
          <Link to={`/protection/sessions?programId=${program._id}`} onClick={onHide}>
            {t('spray.programs.viewSessions', { count: program.sessionNames?.length ?? 0 })}
          </Link>
        </p>

        {(program.rounds?.length ?? 0) > 0 && (
          <>
            <strong>{t('spray.programs.rounds')}</strong>
            <Table bordered size="sm" className="mt-1">
              <thead>
                <tr>
                  <th>{t('spray.programs.roundName')}</th>
                  <th>{t('spray.programs.roundProduct')}</th>
                  <th>{t('spray.programs.roundDate')}</th>
                  <th>{t('spray.programs.roundWater')}</th>
                </tr>
              </thead>
              <tbody>
                {program.rounds.map((r, idx) => {
                  // Read via productList (server-injected) and fall back to
                  // [productId] for legacy rounds. Multi-product rounds render
                  // as comma-joined product names.
                  const list = r.productList || (r.productId ? [r.productId] : []);
                  const names = list
                    .map(id => productMap.get(id))
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <tr key={r._id || idx}>
                      <td>
                        {r.name || ''}
                        <IdChip prefix="R" seq={r.seq} />
                      </td>
                      <td>{names || '—'}</td>
                      <td>{r.plannedDate.substring(0, 10)}</td>
                      <td>{r.waterPerHectare ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onHide}>
          {t('common.close')}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProgramDetailModal;
